import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import CloseGlyph from './CloseGlyph'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { usePresence } from '../hooks/usePresence'
import {
  countActiveFilters,
  DATE_FILTERS,
  sanitizeCartFilters,
  TRI_FILTERS,
  validatePriceRange,
} from '../utils/cartFiltersPolicy'
import './CartFiltersPanel.css'

function PriceGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path d="M7 8.5h7.2a2.7 2.7 0 0 1 0 5.4H9.2M9.2 13.9H15a2.8 2.8 0 0 1 0 5.6H7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M12 6v14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

function ImageGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <rect x="3.5" y="5.5" width="17" height="13" rx="2.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="9" cy="10.2" r="1.4" fill="currentColor" />
      <path d="M5.8 16.2 10 12.6l3.1 2.6 2.4-2.2 2.7 3.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function LinkGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path d="M10.2 13.8a4 4 0 0 1 0-5.7l2.5-2.5a4 4 0 1 1 5.7 5.7l-1.4 1.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M13.8 10.2a4 4 0 0 1 0 5.7l-2.5 2.5a4 4 0 1 1-5.7-5.7l1.4-1.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

function DateGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15" rx="2.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 3.5v4M16 3.5v4M3.5 10h17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

function SegmentedControl({ value, onChange, options, ariaLabel }) {
  const index = Math.max(0, options.findIndex((option) => option.id === value))
  return (
    <div className="cart-filters-segment" role="radiogroup" aria-label={ariaLabel}>
      <span
        className="cart-filters-segment-pill"
        style={{
          width: `${100 / options.length}%`,
          transform: `translateX(${index * 100}%)`,
        }}
      />
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          role="radio"
          aria-checked={value === option.id}
          className={`cart-filters-segment-btn${value === option.id ? ' is-active' : ''}`}
          onClick={() => onChange(option.id)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

export default function CartFiltersPanel({
  isOpen = false,
  onClose,
  anchorRef,
  filters,
  onChange,
  onReset,
  t = (key) => key,
}) {
  const reducedMotion = usePrefersReducedMotion()
  const { mounted, leaving } = usePresence(isOpen, { duration: 300, reduced: reducedMotion })
  const panelRef = useRef(null)
  const [coords, setCoords] = useState({ top: 88, left: 24 })
  const [dateOpen, setDateOpen] = useState(false)
  const value = sanitizeCartFilters(filters)
  const activeCount = countActiveFilters(value)
  const rangeError = validatePriceRange(value.priceMin, value.priceMax)

  useLayoutEffect(() => {
    if (!mounted) return undefined
    const update = () => {
      const summary = document.querySelector('.cart-summary')
      const products = document.querySelector('.cart-detail-container')
      const top = summary ? summary.getBoundingClientRect().top : 96
      const productsLeft = products ? products.getBoundingClientRect().left : 320
      setCoords({
        top: Math.max(16, top),
        left: Math.max(16, productsLeft - 292 - 16),
      })
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [mounted])

  useEffect(() => {
    if (!mounted) {
      setDateOpen(false)
      return undefined
    }
    const onPointer = (event) => {
      if (panelRef.current?.contains(event.target) || anchorRef?.current?.contains?.(event.target)) return
      onClose?.()
    }
    const onKey = (event) => {
      if (event.key === 'Escape') onClose?.()
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [mounted, onClose, anchorRef])

  if (!mounted || typeof document === 'undefined') return null

  const patch = (partial) => onChange?.({ ...value, ...partial })
  const dateLabel = {
    all: t('all') || 'Tous',
    today: t('today') || 'Aujourd’hui',
    week: t('lastWeek') || 'Semaine dernière',
    month: t('lastMonth') || 'Mois dernier',
    year: t('lastYear') || 'Année dernière',
  }[value.dateFilter] || t('all') || 'Tous'

  const triOptions = [
    { id: 'all', label: t('all') || 'Tous' },
    { id: 'with', label: t('filterWith') || 'Avec' },
    { id: 'without', label: t('filterWithout') || 'Sans' },
  ]

  return createPortal(
    <aside
      ref={panelRef}
      className={`lg-panel cart-filters-panel${leaving ? ' is-leaving' : ''}${reducedMotion ? ' is-reduced' : ' is-animated'}`}
      style={{ top: coords.top, left: coords.left }}
      role="dialog"
      aria-label={t('advancedFilters') || 'Filtres'}
    >
      <div className="lg-blur-prewarm" aria-hidden="true" />
      <div className="lg-panel-frost" />
      <div className="lg-panel-tint" />
      <div className="lg-panel-shine" />
      <div className="lg-panel-body cart-filters-panel-body">
        <header className="cart-filters-header">
          <div>
            <h3>{t('advancedFilters') || 'Filtres'}</h3>
            <p>{activeCount ? `${activeCount} actif${activeCount > 1 ? 's' : ''}` : 'Aucun filtre actif'}</p>
          </div>
        </header>

        <div className="cart-filters-group">
          <label>
            <PriceGlyph />
            {t('priceRange') || 'Plage de prix'}
          </label>
          <div className="cart-filters-price-row">
            <input
              className="cart-filters-input"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              placeholder={t('minPrice') || 'Min'}
              value={value.priceMin}
              onChange={(event) => patch({ priceMin: event.target.value })}
              aria-label={t('minPrice') || 'Prix min'}
            />
            <span className="cart-filters-price-sep" aria-hidden="true">–</span>
            <input
              className="cart-filters-input"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              placeholder={t('maxPrice') || 'Max'}
              value={value.priceMax}
              onChange={(event) => patch({ priceMax: event.target.value })}
              aria-label={t('maxPrice') || 'Prix max'}
            />
          </div>
          {rangeError ? <p className="cart-filters-error">{rangeError}</p> : null}
        </div>

        <div className="cart-filters-group">
          <label>
            <ImageGlyph />
            {t('hasImage') || 'Image'}
          </label>
          <SegmentedControl
            value={TRI_FILTERS.includes(value.hasImage) ? value.hasImage : 'all'}
            onChange={(hasImage) => patch({ hasImage })}
            options={triOptions}
            ariaLabel={t('hasImage') || 'Image'}
          />
        </div>

        <div className="cart-filters-group">
          <label>
            <LinkGlyph />
            {t('hasLink') || 'Lien'}
          </label>
          <SegmentedControl
            value={TRI_FILTERS.includes(value.hasLink) ? value.hasLink : 'all'}
            onChange={(hasLink) => patch({ hasLink })}
            options={triOptions}
            ariaLabel={t('hasLink') || 'Lien'}
          />
        </div>

        <div className="cart-filters-group">
          <label>
            <DateGlyph />
            {t('dateAdded') || 'Date d’ajout'}
          </label>
          <button
            type="button"
            className={`cart-filters-date${dateOpen ? ' is-open' : ''}`}
            onClick={() => setDateOpen((open) => !open)}
            aria-expanded={dateOpen}
          >
            {dateLabel}
            <span aria-hidden="true">▾</span>
          </button>
          {dateOpen ? (
            <ul className="cart-filters-date-list">
              {DATE_FILTERS.map((id) => (
                <li key={id}>
                  <button
                    type="button"
                    className={value.dateFilter === id ? 'is-selected' : ''}
                    onClick={() => {
                      patch({ dateFilter: id })
                      setDateOpen(false)
                    }}
                  >
                    {{
                      all: t('all') || 'Tous',
                      today: t('today') || 'Aujourd’hui',
                      week: t('lastWeek') || 'Semaine dernière',
                      month: t('lastMonth') || 'Mois dernier',
                      year: t('lastYear') || 'Année dernière',
                    }[id]}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="cart-filters-actions">
          <button type="button" className="cart-filters-action" onClick={onReset}>
            {t('resetFilters') || 'Réinitialiser'}
          </button>
          <button type="button" className="cart-filters-action is-close" onClick={onClose}>
            <CloseGlyph size={16} />
            {t('close') || 'Fermer'}
          </button>
        </div>
      </div>
    </aside>,
    document.body,
  )
}
